import { inject, Injectable } from "@angular/core";
import { Observable, Subject, Subscription, of } from "rxjs";
import { WebSocketSubject, webSocket } from "rxjs/webSocket";
import { environment } from "../../../../environments/environment";
import { AuthService } from "../../api/auth/auth.service";

/**
 * Produces the complete current subscription set for one raw connection opening.
 * It is called for the initial connection and after a `4401` recovery, so it
 * must read current domain state rather than capture a stale request list.
 */
export type SubscriptionFactory<TRequest> = () => readonly TRequest[];

interface ManagedSubscriptionOptions<TRequest> {
  factory: SubscriptionFactory<TRequest>;
  terminalErrorCloseCodes: readonly number[];
}

/**
 * Transport handle for one endpoint connection. Consumers needing logical
 * subscriptions should use a domain subscription manager instead of sending
 * subscribe messages directly.
 */
export interface WebSocketConnection<TIncoming, TRequest> {
  readonly messages$: Observable<TIncoming>;
  readonly isOpen: boolean;
  send(message: TRequest): void;
  close(): void;
}

/** Raised when a `4401` connection cannot recover an authenticated session. */
export class WebSocketAuthenticationRefreshError extends Error {
  public override readonly cause: unknown;

  constructor(cause: unknown, path: string, version: number) {
    super(`Unable to refresh authentication for WebSocket ${path} (v${version}).`);
    this.name = "WebSocketAuthenticationRefreshError";
    this.cause = cause;
  }
}

/** Raised when a subscription factory cannot prepare the next connection opening. */
export class WebSocketSubscriptionSetupError extends Error {
  public override readonly cause: unknown;

  constructor(cause: unknown, path: string, version: number) {
    super(`Unable to prepare subscriptions for WebSocket ${path} (v${version}).`);
    this.name = "WebSocketSubscriptionSetupError";
    this.cause = cause;
  }
}

/** Raised when a server closes an established connection with an application-specific failure code. */
export class WebSocketConnectionClosedError extends Error {
  constructor(
    public readonly code: number,
    public readonly reason: string,
    path: string,
    version: number
  ) {
    super(`WebSocket ${path} (v${version}) closed with code ${code}${reason ? `: ${reason}` : "."}`);
    this.name = "WebSocketConnectionClosedError";
  }
}

@Injectable({
  providedIn: "root",
})
/**
 * Owns transport-level WebSocket recovery. A `4401` refreshes the session and
 * reconnects; subscription state remains the responsibility of the caller's
 * factory so this service stays independent of endpoint protocols.
 */
export class WebSocketConnectionService {
  private authService = inject(AuthService);

  /**
   * Opens an isolated WebSocket connection. Domain services own any sharing and
   * subscription bookkeeping; this service only manages the connection lifecycle.
   * The factory is invoked on every raw connection, including after `4401`.
   */
  open<TIncoming, TRequest>(
    path: string,
    version: number,
    subscriptionFactory: SubscriptionFactory<TRequest>,
    terminalErrorCloseCodes: readonly number[] = []
  ): WebSocketConnection<TIncoming, TRequest> {
    return new ManagedWebSocketConnection<TIncoming, TRequest>(
      path,
      version,
      { factory: subscriptionFactory, terminalErrorCloseCodes },
      this.authService,
      (socketPath, socketVersion, token) => this.buildUrl(socketPath, socketVersion, token)
    );
  }

  private buildUrl(path: string, version: number, token: string): string {
    const baseUrl = version === 2 ? environment.webSocketBaseUrlV2 : environment.webSocketBaseUrl;
    return `${baseUrl}/${path}?Authorization=${token}`;
  }
}

/**
 * Keeps a stable incoming stream while a `4401` refreshes and replaces the raw
 * connection. Any other terminal connection failure is propagated to its consumers.
 */
class ManagedWebSocketConnection<TIncoming, TRequest> implements WebSocketConnection<TIncoming, TRequest> {
  private readonly messages = new Subject<TIncoming>();
  private rawConnection?: WebSocketSubject<TRequest>;
  private rawConnectionSubscription?: Subscription;
  private recoverySubscription?: Subscription;
  private currentConnectionGeneration = 0;
  private disposed = false;
  private recovering = false;
  private connectionOpen = false;

  readonly messages$ = this.messages.asObservable();

  get isOpen(): boolean {
    return this.connectionOpen && !this.disposed;
  }

  constructor(
    private readonly path: string,
    private readonly version: number,
    private readonly subscriptionOptions: ManagedSubscriptionOptions<TRequest>,
    private readonly authService: AuthService,
    private readonly buildUrl: (path: string, version: number, token: string) => string
  ) {
    this.openConnection();
  }

  send(message: TRequest): void {
    if (this.isOpen) {
      this.rawConnection?.next(message);
    }
  }

  close(): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.connectionOpen = false;
    this.currentConnectionGeneration++;
    this.recoverySubscription?.unsubscribe();
    this.rawConnectionSubscription?.unsubscribe();
    this.rawConnection?.complete();
    this.messages.complete();
  }

  private openConnection(): void {
    if (this.disposed) {
      return;
    }

    const token = this.authService.getToken();
    if (!token || !this.authService.hasValidAccessToken(token)) {
      this.terminate(
        new WebSocketAuthenticationRefreshError(new Error("No valid access token."), this.path, this.version)
      );
      return;
    }

    const connectionGeneration = ++this.currentConnectionGeneration;
    this.recovering = false;
    this.connectionOpen = false;

    const rawConnection = webSocket<TRequest>({
      url: this.buildUrl(this.path, this.version, token),
      openObserver: {
        next: () => {
          if (!this.isCurrentConnection(connectionGeneration, rawConnection)) {
            return;
          }

          this.connectionOpen = true;
          try {
            for (const message of this.subscriptionOptions.factory()) {
              rawConnection.next(message);
            }
          } catch (error) {
            this.terminate(new WebSocketSubscriptionSetupError(error, this.path, this.version));
          }
        },
      },
      closeObserver: {
        next: (event) => {
          if (!this.isCurrentConnection(connectionGeneration, rawConnection)) {
            return;
          }

          if (event.code === 4401) {
            this.recovering = true;
            this.connectionOpen = false;
            this.recover(connectionGeneration, rawConnection, token);
          } else if (this.subscriptionOptions.terminalErrorCloseCodes.includes(event.code)) {
            this.terminate(new WebSocketConnectionClosedError(event.code, event.reason, this.path, this.version));
          }
        },
      },
    });

    this.rawConnection = rawConnection;
    this.rawConnectionSubscription = rawConnection.subscribe({
      next: (message) => {
        if (this.isCurrentConnection(connectionGeneration, rawConnection)) {
          this.messages.next(message as unknown as TIncoming);
        }
      },
      error: (error) => {
        if (this.isCurrentConnection(connectionGeneration, rawConnection) && !this.recovering) {
          this.terminate(error);
        }
      },
      complete: () => {
        if (this.isCurrentConnection(connectionGeneration, rawConnection) && !this.recovering) {
          this.terminate();
        }
      },
    });
  }

  private recover(connectionGeneration: number, rawConnection: WebSocketSubject<TRequest>, openingToken: string): void {
    const canReuseCurrentToken = this.authService.getToken() !== openingToken && this.authService.hasValidAccessToken();
    const recovery$ = canReuseCurrentToken ? of(undefined) : this.authService.refreshSession();

    this.recoverySubscription = recovery$.subscribe({
      next: () => {
        if (this.isCurrentConnection(connectionGeneration, rawConnection)) {
          this.openConnection();
        }
      },
      error: (error) => {
        if (this.isCurrentConnection(connectionGeneration, rawConnection)) {
          this.terminate(new WebSocketAuthenticationRefreshError(error, this.path, this.version));
        }
      },
    });
  }

  private isCurrentConnection(connectionGeneration: number, rawConnection: WebSocketSubject<TRequest>): boolean {
    return (
      !this.disposed &&
      this.currentConnectionGeneration === connectionGeneration &&
      this.rawConnection === rawConnection
    );
  }

  private terminate(error?: unknown): void {
    if (this.disposed) {
      return;
    }

    this.disposed = true;
    this.connectionOpen = false;
    this.currentConnectionGeneration++;
    this.recoverySubscription?.unsubscribe();
    this.rawConnectionSubscription?.unsubscribe();
    this.rawConnection?.complete();

    if (error) {
      this.messages.error(error);
    } else {
      this.messages.complete();
    }
  }
}
